import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User,
  Settings,
  Flame,
  Plus,
  Home as HomeIcon,
  TrendingUp,
  Activity,
  Share2,
  Coins,
  Download,
  Shield,
  ClipboardList,
  ChevronRight,
  Sparkles,
  Target,
  X,
  Trash2,
  Crown
} from 'lucide-react';
import CircularProgress from '../components/CircularProgress';
import WeekCalendar from '../components/WeekCalendar';
import FoodEntrySelection from '../components/FoodEntrySelection';
import ManualFoodEntry from '../components/ManualFoodEntry';
import AICameraScanner from '../components/AICameraScanner';
import AIResultsReview from '../components/AIResultsReview';
import { useRefreshOnFocus } from '../utils/pwa-helpers';
import LongevityBot from '../components/LongevityBot';
import NotificationCard from '../components/NotificationCard';
import ProBadge from '../components/ProBadge';
import CompleteProfileModal from '../components/CompleteProfileModal';
import GenerateProtocolModal from '../components/GenerateProtocolModal';
import OnboardingBanner from '../components/OnboardingBanner';
import FutureLookButton from '../components/FutureLookButton';
import { supabase } from '../lib/supabase';
import { useDataCache } from '../contexts/DataCacheContext';
import { useFestivalTheme } from '../contexts/FestivalThemeContext';
import { FestivalDecorations, FestivalBanner } from '../components/FestivalDecorations';
import Onboarding from './Onboarding';
import Profile from './Profile';
import Progress from './Progress';
import ActivityPage from './Activity';
import AdminPanel from './AdminPanel';
import Subscription from './Subscription';
import TodaysRoutine from './TodaysRoutine';
import ProtocolView from './ProtocolView';
import CustomHabits from './CustomHabits';
import GiftRedemptionBanner from '../components/GiftRedemptionBanner';

interface FoodLog {
  id: string;
  meal_name: string;
  image_url: string | null;
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  logged_at: string;
}

interface NutritionGoals {
  calorie_goal: number;
  protein_goal_g: number;
  carbs_goal_g: number;
  fat_goal_g: number;
}

const formatDateForDB = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isPastDate = (date: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return compareDate < today;
};

// Delete Confirmation Modal Component
function DeleteFoodModal({ 
  food, 
  onClose, 
  onConfirm,
  isDeleting 
}: { 
  food: FoodLog; 
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}) {
  const [isClosing, setIsClosing] = useState(false);
  const { currentTheme, currentThemeId } = useFestivalTheme();

  const handleClose = () => {
    if (isDeleting) return;
    setIsClosing(true);
    setTimeout(onClose, 300);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-end justify-center transition-all duration-300 ${
        isClosing ? 'bg-black/0' : 'bg-black/50'
      }`}
      onClick={handleBackdropClick}
    >
      <div
        className={`w-full max-w-lg bg-white rounded-t-[28px] transform transition-transform duration-300 ease-out ${
          isClosing ? 'translate-y-full' : 'translate-y-0'
        }`}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        <div className="px-5 pb-6">
          {/* Icon */}
          <div className="text-center mb-4">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Trash2 size={32} className="text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Delete Food?</h2>
            <p className="text-sm text-gray-500 mt-1">This action cannot be undone</p>
          </div>

          {/* Food Preview */}
          <div className="bg-gray-50 rounded-xl p-3 mb-5 flex items-center gap-3">
            <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center text-xl flex-shrink-0 overflow-hidden">
              {food.image_url ? (
                <img src={food.image_url} alt={food.meal_name} className="w-full h-full object-cover" />
              ) : (
                '🍽️'
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm text-gray-900 truncate">{food.meal_name}</p>
              <p className="text-xs text-gray-500">{food.calories} calories</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleClose}
              disabled={isDeleting}
              className="py-3.5 rounded-xl font-bold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all active:scale-[0.98] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isDeleting}
              className="py-3.5 rounded-xl font-bold text-sm bg-red-500 hover:bg-red-600 text-white transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 size={16} />
                  Delete
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Food Detail Modal Component
function FoodDetailModal({ food, onClose }: { food: FoodLog; onClose: () => void }) {
  const [isClosing, setIsClosing] = useState(false);
  const { currentTheme, currentThemeId } = useFestivalTheme();

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, 300);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Calculate macro percentages
  const totalMacroCalories = (food.protein_g * 4) + (food.carbs_g * 4) + (food.fat_g * 9);
  const proteinPercent = totalMacroCalories > 0 ? Math.round(((food.protein_g * 4) / totalMacroCalories) * 100) : 0;
  const carbsPercent = totalMacroCalories > 0 ? Math.round(((food.carbs_g * 4) / totalMacroCalories) * 100) : 0;
  const fatPercent = totalMacroCalories > 0 ? Math.round(((food.fat_g * 9) / totalMacroCalories) * 100) : 0;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-end justify-center transition-all duration-300 ${
        isClosing ? 'bg-black/0' : 'bg-black/50'
      }`}
      onClick={handleBackdropClick}
    >
      <div
        className={`w-full max-w-lg bg-white rounded-t-[28px] transform transition-transform duration-300 ease-out ${
          isClosing ? 'translate-y-full' : 'translate-y-0'
        }`}
        style={{ maxHeight: '90vh' }}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors z-10"
        >
          <X size={16} className="text-gray-600" />
        </button>

        <div className="px-5 pb-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 50px)' }}>
          {/* Food Image */}
          <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-orange-100 to-amber-100 mb-4">
            {food.image_url ? (
              <img
                src={food.image_url}
                alt={food.meal_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="text-5xl">🍽️</span>
              </div>
            )}
          </div>

          {/* Food Name & Time */}
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-900 leading-tight">{food.meal_name}</h2>
            <p className="text-sm text-gray-500 mt-1">
              Logged at {new Date(food.logged_at).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              })}
            </p>
          </div>

          {/* Calories Card - Festival Themed */}
          <div 
            className="rounded-2xl p-4 mb-4"
            style={{
              background: currentThemeId !== 'none'
                ? `linear-gradient(135deg, ${currentTheme.gradientFrom}15, ${currentTheme.gradientTo}15)`
                : 'linear-gradient(135deg, #fff7ed, #fef3c7)',
              border: currentThemeId !== 'none'
                ? `1px solid ${currentTheme.primaryColor}30`
                : '1px solid #fed7aa'
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p 
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{ color: currentThemeId !== 'none' ? currentTheme.primaryColor : '#ea580c' }}
                >
                  Total Calories
                </p>
                <p className="text-3xl font-black text-gray-900 mt-0.5">{food.calories}</p>
              </div>
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{
                  background: currentThemeId !== 'none'
                    ? `${currentTheme.primaryColor}20`
                    : '#ffedd5'
                }}
              >
                {currentThemeId !== 'none' ? (
                  <span className="text-xl">{currentTheme.emoji}</span>
                ) : (
                  <Flame size={24} className="text-orange-500" />
                )}
              </div>
            </div>
          </div>

          {/* Macros Grid */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {/* Protein */}
            <div className="bg-red-50 border border-red-100 rounded-xl p-3">
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mb-2">
                <span className="text-base">🍗</span>
              </div>
              <p className="text-[10px] text-red-600 font-semibold uppercase tracking-wide">Protein</p>
              <p className="text-xl font-bold text-gray-900">{food.protein_g}g</p>
            </div>

            {/* Carbs */}
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mb-2">
                <span className="text-base">🌾</span>
              </div>
              <p className="text-[10px] text-amber-600 font-semibold uppercase tracking-wide">Carbs</p>
              <p className="text-xl font-bold text-gray-900">{food.carbs_g}g</p>
            </div>

            {/* Fat */}
            <div className="bg-violet-50 border border-violet-100 rounded-xl p-3">
              <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center mb-2">
                <span className="text-base">🫐</span>
              </div>
              <p className="text-[10px] text-violet-600 font-semibold uppercase tracking-wide">Fat</p>
              <p className="text-xl font-bold text-gray-900">{food.fat_g}g</p>
            </div>
          </div>

          {/* Macro Distribution Bar */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-700 mb-3">Macro Distribution</p>
            <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden flex">
              <div
                className="h-full bg-red-400 transition-all"
                style={{ width: `${proteinPercent}%` }}
              />
              <div
                className="h-full bg-amber-400 transition-all"
                style={{ width: `${carbsPercent}%` }}
              />
              <div
                className="h-full bg-violet-400 transition-all"
                style={{ width: `${fatPercent}%` }}
              />
            </div>
            <div className="flex justify-between mt-3 text-[11px] text-gray-600">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-400"></span>
                <span className="font-medium">Protein {proteinPercent}%</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                <span className="font-medium">Carbs {carbsPercent}%</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-violet-400"></span>
                <span className="font-medium">Fat {fatPercent}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Food Card Component with Long Press
function FoodCard({ 
  food, 
  onClick, 
  onLongPress,
  onImageClick 
}: { 
  food: FoodLog; 
  onClick: () => void;
  onLongPress: () => void;
  onImageClick: () => void;
}) {
  const longPressTimer = useRef<number | null>(null);
  const isLongPress = useRef(false);
  const { currentTheme, currentThemeId } = useFestivalTheme();

  const handleTouchStart = () => {
    isLongPress.current = false;
    longPressTimer.current = window.setTimeout(() => {
      isLongPress.current = true;
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      onLongPress();
    }, 500);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  };

  const handleTouchMove = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  };

  const handleMouseDown = () => {
    isLongPress.current = false;
    longPressTimer.current = window.setTimeout(() => {
      isLongPress.current = true;
      onLongPress();
    }, 500);
  };

  const handleMouseUp = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  };

  const handleMouseLeave = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  };

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isLongPress.current) {
      onImageClick();
    }
  };

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      className="flex items-center gap-3 p-3 rounded-xl border transition-all select-none"
      style={{
        borderColor: currentThemeId !== 'none' ? `${currentTheme.primaryColor}20` : '#e5e7eb',
        background: currentThemeId !== 'none' ? `${currentTheme.primaryColor}05` : 'white'
      }}
    >
      <div 
        onClick={handleImageClick}
        className="w-16 h-16 rounded-lg bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center text-2xl flex-shrink-0 overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
      >
        {food.image_url ? (
          <img src={food.image_url} alt={food.meal_name} className="w-full h-full object-cover" />
        ) : (
          '🍽️'
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-bold text-sm text-gray-900 truncate">{food.meal_name}</h3>
        <p className="text-xs text-gray-500 mt-1">
          {new Date(food.logged_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
        </p>
        <div className="flex items-center gap-3 mt-2 text-xs">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-gray-900"></span>
            <span className="font-semibold">{food.calories} Calories</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500"></span>
            <span>{food.protein_g}g</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-orange-500"></span>
            <span>{food.carbs_g}g</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
            <span>{food.fat_g}g</span>
          </span>
        </div>
      </div>
      <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
    </div>
  );
}

export default function Home() {
  const { userProfile, loadUserProfile, loadStreakData } = useDataCache();
  const { currentTheme, currentThemeId } = useFestivalTheme();

  const [isLoading, setIsLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [showRoutine, setShowRoutine] = useState(false);
  const [currentPage, setCurrentPage] = useState<'home' | 'progress' | 'activity'>('home');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [foodLogs, setFoodLogs] = useState<FoodLog[]>([]);
  const [nutritionGoals, setNutritionGoals] = useState<NutritionGoals>({
    calorie_goal: 2500,
    protein_goal_g: 162,
    carbs_goal_g: 275,
    fat_goal_g: 70,
  });
  const [totalCalories, setTotalCalories] = useState(0);
  const [totalProtein, setTotalProtein] = useState(0);
  const [totalCarbs, setTotalCarbs] = useState(0);
  const [totalFat, setTotalFat] = useState(0);
  const [showAddFood, setShowAddFood] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showAIScanner, setShowAIScanner] = useState(false);
  const [showAIReview, setShowAIReview] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string>('');
  const [aiResults, setAiResults] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [showCompleteProfileModal, setShowCompleteProfileModal] = useState(false);
  const [protocolGenerated, setProtocolGenerated] = useState<boolean | null>(null);
  const [showGenerateProtocol, setShowGenerateProtocol] = useState(false);
  const [showProtocolView, setShowProtocolView] = useState(false);
  const [showCustomHabits, setShowCustomHabits] = useState(false);
  const [selectedFood, setSelectedFood] = useState<FoodLog | null>(null);
  const [foodToDelete, setFoodToDelete] = useState<FoodLog | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Check if selected date is in the past
  const isSelectedDatePast = isPastDate(selectedDate);

  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstallable(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  useEffect(() => {
    if (userProfile) {
      loadNutritionGoals();
      loadFoodLogs();
      loadStreakData();
      checkProtocolStatus();
    }
  }, [selectedDate, userProfile]);

  useEffect(() => {
    calculateTotals();
  }, [foodLogs]);

  const refreshData = useCallback(() => {
    console.log('[PWA] Refreshing home data...');
    loadUserProfile(true);
    loadStreakData(true);
    if (userProfile) {
      loadNutritionGoals();
      loadFoodLogs();
      checkProtocolStatus();
    }
  }, [loadUserProfile, loadStreakData, userProfile]);

  useRefreshOnFocus(refreshData);

  const handleGiftRedeemed = useCallback(() => {
    console.log('[Gift] Gift redeemed, refreshing user profile...');
    loadUserProfile(true);
  }, [loadUserProfile]);
  
  const loadNutritionGoals = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const dateStr = formatDateForDB(selectedDate);

      const { data: protocol } = await supabase
        .from('user_protocols')
        .select('protocol_data, protocol_generated')
        .eq('user_id', user.id)
        .maybeSingle();

      let protocolCalories = 2500;
      let protocolProtein = 162;
      let protocolCarbs = 275;
      let protocolFat = 70;
      let hasProtocol = false;

      if (protocol && protocol.protocol_generated && protocol.protocol_data?.diet_plan) {
        protocolCalories = protocol.protocol_data.diet_plan.daily_calories || 2500;
        protocolProtein = protocol.protocol_data.diet_plan.protein_g || 162;
        protocolCarbs = protocol.protocol_data.diet_plan.carbs_g || 275;
        protocolFat = protocol.protocol_data.diet_plan.fat_g || 70;
        hasProtocol = true;
      }

      const { data: goals } = await supabase
        .from('daily_nutrition_goals')
        .select('*')
        .eq('user_id', user.id)
        .eq('date', dateStr)
        .maybeSingle();

      if (goals) {
        const needsUpdate = hasProtocol && (
          goals.calorie_goal !== protocolCalories ||
          goals.protein_goal_g !== protocolProtein ||
          goals.carbs_goal_g !== protocolCarbs ||
          goals.fat_goal_g !== protocolFat
        );

        if (needsUpdate) {
          await supabase
            .from('daily_nutrition_goals')
            .update({
              calorie_goal: protocolCalories,
              protein_goal_g: protocolProtein,
              carbs_goal_g: protocolCarbs,
              fat_goal_g: protocolFat,
            })
            .eq('user_id', user.id)
            .eq('date', dateStr);

          setNutritionGoals({
            calorie_goal: protocolCalories,
            protein_goal_g: protocolProtein,
            carbs_goal_g: protocolCarbs,
            fat_goal_g: protocolFat,
          });
        } else {
          setNutritionGoals({
            calorie_goal: goals.calorie_goal,
            protein_goal_g: goals.protein_goal_g,
            carbs_goal_g: goals.carbs_goal_g,
            fat_goal_g: goals.fat_goal_g,
          });
        }
      } else {
        await supabase.from('daily_nutrition_goals').insert({
          user_id: user.id,
          date: dateStr,
          calorie_goal: protocolCalories,
          protein_goal_g: protocolProtein,
          carbs_goal_g: protocolCarbs,
          fat_goal_g: protocolFat,
        });

        setNutritionGoals({
          calorie_goal: protocolCalories,
          protein_goal_g: protocolProtein,
          carbs_goal_g: protocolCarbs,
          fat_goal_g: protocolFat,
        });
      }
    } catch (error) {
      console.error('Error loading nutrition goals:', error);
    }
  };

  const loadFoodLogs = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const dateStr = formatDateForDB(selectedDate);

      const { data: logs } = await supabase
        .from('food_logs')
        .select('*')
        .eq('user_id', user.id)
        .eq('date', dateStr)
        .order('logged_at', { ascending: false });

      if (logs) {
        setFoodLogs(logs);
      }
    } catch (error) {
      console.error('Error loading food logs:', error);
    }
  };

  const checkProtocolStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: protocol } = await supabase
        .from('user_protocols')
        .select('protocol_generated')
        .eq('user_id', user.id)
        .maybeSingle();

      if (protocol) {
        setProtocolGenerated(protocol.protocol_generated || false);
      } else {
        setProtocolGenerated(false);
      }
    } catch (error) {
      console.error('Error checking protocol status:', error);
      setProtocolGenerated(false);
    }
  };

  const handleProtocolGenerated = () => {
    setShowGenerateProtocol(false);
    setProtocolGenerated(true);
    setShowRoutine(true);
  };

  const calculateTotals = () => {
    const calories = foodLogs.reduce((sum, log) => sum + log.calories, 0);
    const protein = foodLogs.reduce((sum, log) => sum + log.protein_g, 0);
    const carbs = foodLogs.reduce((sum, log) => sum + log.carbs_g, 0);
    const fat = foodLogs.reduce((sum, log) => sum + log.fat_g, 0);

    setTotalCalories(calories);
    setTotalProtein(protein);
    setTotalCarbs(carbs);
    setTotalFat(fat);
  };

  const handleManualSave = async (foodData: {
    name: string;
    calories: string;
    protein: string;
    carbs: string;
    fat: string;
  }) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const dateStr = formatDateForDB(selectedDate);

      const { data: newLog } = await supabase
        .from('food_logs')
        .insert({
          user_id: user.id,
          date: dateStr,
          meal_name: foodData.name,
          calories: parseInt(foodData.calories) || 0,
          protein_g: parseInt(foodData.protein) || 0,
          carbs_g: parseInt(foodData.carbs) || 0,
          fat_g: parseInt(foodData.fat) || 0,
        })
        .select()
        .single();

      if (newLog) {
        setFoodLogs([newLog, ...foodLogs]);
        setShowManualEntry(false);
        setShowAddFood(false);
      }
    } catch (error) {
      console.error('Error adding food:', error);
    }
  };

  const handleImageCapture = async (imageData: string) => {
    setCapturedImage(imageData);
    setShowAIScanner(false);
    setIsAnalyzing(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-food-image`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ imageData }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to analyze image');
      }

      const results = await response.json();
      setAiResults(results);
      setIsAnalyzing(false);
      setShowAIReview(true);
    } catch (error) {
      console.error('Error analyzing image:', error);
      setIsAnalyzing(false);
      alert('Failed to analyze the image. Please try again or use manual entry.');
      setShowAddFood(true);
    }
  };

  const handleAISave = async (foodData: {
    name: string;
    calories: string;
    protein: string;
    carbs: string;
    fat: string;
    imageData: string;
  }) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const dateStr = formatDateForDB(selectedDate);

      let imageUrl = null;

      if (foodData.imageData) {
        const base64Data = foodData.imageData.split(',')[1];
        const fileName = `${user.id}/${Date.now()}.jpg`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('food-images')
          .upload(fileName, decode(base64Data), {
            contentType: 'image/jpeg',
          });

        if (uploadError) {
          console.error('Error uploading image:', uploadError);
        } else {
          const { data: urlData } = supabase.storage
            .from('food-images')
            .getPublicUrl(fileName);
          imageUrl = urlData.publicUrl;
        }
      }

      const { data: newLog } = await supabase
        .from('food_logs')
        .insert({
          user_id: user.id,
          date: dateStr,
          meal_name: foodData.name,
          calories: parseInt(foodData.calories) || 0,
          protein_g: parseInt(foodData.protein) || 0,
          carbs_g: parseInt(foodData.carbs) || 0,
          fat_g: parseInt(foodData.fat) || 0,
          image_url: imageUrl,
        })
        .select()
        .single();

      if (newLog) {
        setFoodLogs([newLog, ...foodLogs]);
        setShowAIReview(false);
        setShowAddFood(false);
        setCapturedImage('');
        setAiResults(null);
      }
    } catch (error) {
      console.error('Error adding food:', error);
    }
  };

  const handleDeleteFood = async () => {
    if (!foodToDelete) return;
    
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('food_logs')
        .delete()
        .eq('id', foodToDelete.id);

      if (!error) {
        setFoodLogs(foodLogs.filter(log => log.id !== foodToDelete.id));
        setFoodToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting food:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRetakePhoto = () => {
    setShowAIReview(false);
    setShowAIScanner(true);
    setCapturedImage('');
    setAiResults(null);
  };

  const closeAllModals = () => {
    setShowAddFood(false);
    setShowManualEntry(false);
    setShowAIScanner(false);
    setShowAIReview(false);
    setCapturedImage('');
    setAiResults(null);
  };

  const handleLockedFeatureClick = () => {
    if (!userProfile?.onboarding_completed) {
      setShowCompleteProfileModal(true);
    }
  };

  const handleStartOnboarding = () => {
    setShowCompleteProfileModal(false);
    setShowOnboarding(true);
  };

  const handleFoodCardClick = (food: FoodLog) => {
    setSelectedFood(food);
  };

  const handleFoodCardLongPress = (food: FoodLog) => {
    setFoodToDelete(food);
  };

  function decode(base64: string): Uint8Array {
    const binString = atob(base64);
    const len = binString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binString.charCodeAt(i);
    }
    return bytes;
  }

  const checkOnboardingStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        window.location.href = '/';
        return;
      }

      await loadUserProfile();
      await loadStreakData();

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!profile) {
        setNeedsOnboarding(true);
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  const handleCompleteOnboarding = async () => {
    setShowOnboarding(false);
    setNeedsOnboarding(false);
    setIsLoading(true);
    await loadUserProfile(true);
    await loadStreakData(true);
    await checkOnboardingStatus();
  };

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setIsInstallable(false);
    }
    setDeferredPrompt(null);
  };

  // Loading Screen
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center overflow-hidden">
        <div className="flex flex-col items-center -mt-16">
          <LongevityBot size={200} mode="splash" />
        </div>
        <p className="text-sm text-black absolute bottom-1/4">Getting things ready...</p>
      </div>
    );
  }

  if (needsOnboarding || showOnboarding) {
    return <Onboarding onComplete={handleCompleteOnboarding} onSkip={handleCompleteOnboarding} />;
  }

  if (!userProfile) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center overflow-hidden">
        <div className="flex flex-col items-center -mt-16">
          <LongevityBot size={200} mode="splash" />
        </div>
        <p className="text-xs text-gray-400 absolute bottom-1/3">Getting things ready...</p>
      </div>
    );
  }

  if (showProfile && userProfile) {
    return (
      <Profile
        profile={userProfile}
        onBack={() => setShowProfile(false)}
        onUpdate={() => loadUserProfile(true)}
        onStartOnboarding={() => {
          setShowProfile(false);
          setShowOnboarding(true);
        }}
        onNavigate={(page) => {
          setShowProfile(false);
          if (page === 'home' || page === 'progress' || page === 'activity') {
            setCurrentPage(page);
          }
        }}
        onOpenAdminPanel={() => {
          setShowProfile(false);
          setShowAdminPanel(true);
        }}
      />
    );
  }

  if (currentPage === 'progress') {
    return (
      <Progress
        onBack={() => setCurrentPage('home')}
        onNavigate={(page) => {
          if (page === 'home') setCurrentPage('home');
          else if (page === 'activity') setCurrentPage('activity');
          else if (page === 'profile') setShowProfile(true);
        }}
        onOpenSubscription={() => setShowSubscription(true)}
        onStartOnboarding={handleStartOnboarding}
      />
    );
  }

  if (currentPage === 'activity') {
    return (
      <ActivityPage
        onBack={() => setCurrentPage('home')}
        onNavigate={(page) => {
          if (page === 'home') setCurrentPage('home');
          else if (page === 'progress') setCurrentPage('progress');
          else if (page === 'profile') setShowProfile(true);
        }}
        onOpenSubscription={() => setShowSubscription(true)}
        onStartOnboarding={handleStartOnboarding}
      />
    );
  }

  if (showAdminPanel) {
    return <AdminPanel onBack={() => setShowAdminPanel(false)} />;
  }

  if (showSubscription) {
    return <Subscription onBack={() => setShowSubscription(false)} />;
  }

  if (showRoutine) {
    return (
      <TodaysRoutine
        onBack={() => setShowRoutine(false)}
        selectedDate={selectedDate}
        onNavigate={(page) => {
          setShowRoutine(false);
          if (page === 'home') setCurrentPage('home');
          else if (page === 'progress') setCurrentPage('progress');
          else if (page === 'activity') setCurrentPage('activity');
          else if (page === 'profile') setShowProfile(true);
        }}
      />
    );
  }

  if (showProtocolView) {
    return (
      <ProtocolView
        onBack={() => setShowProtocolView(false)}
        onNavigate={(page) => {
          setShowProtocolView(false);
          if (page === 'home') setCurrentPage('home');
          else if (page === 'progress') setCurrentPage('progress');
          else if (page === 'activity') setCurrentPage('activity');
          else if (page === 'profile') setShowProfile(true);
        }}
      />
    );
  }

  if (showCustomHabits) {
    return <CustomHabits onBack={() => setShowCustomHabits(false)} />;
  }

  return (
    <>
      {/* Festival Banner - Shows at very top when theme is active */}
      <FestivalBanner />
      
      <div className="min-h-screen bg-gray-50 pb-20 relative">
        {/* Festival Decorations - Particles, floating emojis, corner decorations */}
        <FestivalDecorations 
          showParticles={true}
          showCorners={true}
          showFloating={true}
          particleCount={15}
          containerHeight={1200}
        />
        
        <div className="max-w-2xl mx-auto relative z-20">
          {/* Header with Festival Theme and Pro Badge */}
          <header 
            className="px-6 py-4 flex items-center justify-between sticky top-0 z-10 border-b transition-all"
            style={{
              background: currentThemeId !== 'none' 
                ? `linear-gradient(135deg, ${currentTheme.gradientFrom}10, ${currentTheme.gradientTo}10, white)`
                : 'white',
              borderColor: currentThemeId !== 'none' 
                ? `${currentTheme.primaryColor}20`
                : '#e5e7eb'
            }}
          >
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{
                  background: currentThemeId !== 'none'
                    ? `linear-gradient(135deg, ${currentTheme.gradientFrom}, ${currentTheme.gradientTo})`
                    : 'linear-gradient(135deg, #06b6d4, #3b82f6)'
                }}
              >
                {currentThemeId !== 'none' ? (
                  <span className="text-lg">{currentTheme.emoji}</span>
                ) : (
                  <Activity className="w-5 h-5 text-white" strokeWidth={2.5} />
                )}
              </div>
              <div>
                {/* PRO BADGE - Shows above name for Pro users */}
                {userProfile.is_pro_member ? (
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <div className="w-4 h-4 bg-gradient-to-r from-amber-400 to-yellow-500 rounded flex items-center justify-center">
                      <Crown className="w-2.5 h-2.5 text-white" />
                    </div>
                    <span className="text-amber-600 text-xs font-semibold tracking-wide">PRO</span>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">
                    {currentThemeId !== 'none'
                      ? currentTheme.description
                      : "Let's get healthier today"}
                  </p>
                )}
                <h1 className="text-xl font-bold text-gray-900">
                  {currentThemeId !== 'none' 
                    ? `${currentTheme.heroBadgeEmoji} Welcome, ${userProfile.name.split(' ')[0]}`
                    : `Welcome, ${userProfile.name.split(' ')[0]}`}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Show ProBadge button only for non-pro users */}
              {!userProfile.is_pro_member && (
                <ProBadge onClick={() => setShowSubscription(true)} />
              )}
              {userProfile.is_admin && (
                <button
                  onClick={() => setShowAdminPanel(true)}
                  className="w-10 h-10 rounded-lg bg-yellow-100 hover:bg-yellow-200 flex items-center justify-center transition-colors group"
                  title="Admin Panel"
                >
                  <Shield size={18} className="text-yellow-700" />
                </button>
              )}
            </div>
          </header>

          <div className="p-4 space-y-4">
            <NotificationCard />
            <GiftRedemptionBanner onRedeemed={handleGiftRedeemed} />

            {!userProfile.onboarding_completed && (
              <OnboardingBanner onComplete={handleStartOnboarding} />
            )}

            <WeekCalendar
              selectedDate={selectedDate}
              onDateSelect={setSelectedDate}
            />

            {/* Protocol Generation or Today's Routine Button */}
            {protocolGenerated === null ? (
              <div className="w-full h-24 bg-gray-100 rounded-2xl border border-gray-200 animate-pulse"></div>
            ) : !protocolGenerated && userProfile.onboarding_completed ? (
              <button
                onClick={() => setShowGenerateProtocol(true)}
                className="w-full rounded-2xl p-5 flex items-center justify-between border shadow-lg transition-all active:scale-[0.99]"
                style={{
                  background: currentThemeId !== 'none'
                    ? `linear-gradient(135deg, ${currentTheme.gradientFrom}, ${currentTheme.gradientTo})`
                    : 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                  borderColor: currentThemeId !== 'none'
                    ? currentTheme.primaryColor
                    : '#06b6d4',
                  boxShadow: currentThemeId !== 'none'
                    ? `0 10px 40px ${currentTheme.primaryColor}30`
                    : '0 10px 40px rgba(6, 182, 212, 0.2)'
                }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                    {currentThemeId !== 'none' ? (
                      <span className="text-2xl">{currentTheme.emoji}</span>
                    ) : (
                      <Sparkles className="w-6 h-6 text-white" strokeWidth={2.5} />
                    )}
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-white font-bold text-base">
                        {currentThemeId !== 'none' 
                          ? `${currentTheme.name} Special Protocol`
                          : 'Get Personalised Protocol'}
                      </h3>
                      <span className="px-2 py-0.5 bg-white/30 rounded-full text-[10px] font-bold text-white uppercase">
                        {currentThemeId !== 'none' ? currentTheme.emoji : 'AI'}
                      </span>
                    </div>
                    <p className="text-white/80 text-xs">Your 21-day plan to optimal health</p>
                  </div>
                </div>
                <ChevronRight className="w-6 h-6 text-white" strokeWidth={2.5} />
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    if (!userProfile.onboarding_completed) {
                      handleLockedFeatureClick();
                    } else {
                      setShowRoutine(true);
                    }
                  }}
                  className="w-full rounded-2xl p-4 flex items-center justify-between border transition-all active:scale-[0.99]"
                  style={{
                    background: currentThemeId !== 'none'
                      ? `linear-gradient(135deg, white, ${currentTheme.primaryColor}05)`
                      : 'white',
                    borderColor: currentThemeId !== 'none'
                      ? `${currentTheme.primaryColor}30`
                      : '#e5e7eb'
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{
                        background: currentThemeId !== 'none'
                          ? `${currentTheme.primaryColor}15`
                          : '#ecfeff'
                      }}
                    >
                      {currentThemeId !== 'none' ? (
                        <span className="text-lg">{currentTheme.decorativeEmojis[0]}</span>
                      ) : (
                        <ClipboardList className="w-5 h-5 text-cyan-600" strokeWidth={2} />
                      )}
                    </div>
                    <div className="text-left">
                      <h3 className="text-gray-900 font-semibold text-sm">Today's Routine</h3>
                      <p className="text-gray-500 text-xs">View your daily plan</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" strokeWidth={2} />
                </button>

                {/* Grid Layout */}
                <div className="grid grid-cols-2 gap-3">
                  {/* View Protocol Button */}
                  {protocolGenerated && (
                    <button
                      onClick={() => setShowProtocolView(true)}
                      className="rounded-2xl p-4 flex flex-col justify-between border transition-all active:scale-[0.99]"
                      style={{
                        background: currentThemeId !== 'none'
                          ? `linear-gradient(135deg, white, ${currentTheme.primaryColor}05)`
                          : 'white',
                        borderColor: currentThemeId !== 'none'
                          ? `${currentTheme.primaryColor}30`
                          : '#e5e7eb'
                      }}
                    >
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center mb-2"
                        style={{
                          background: currentThemeId !== 'none'
                            ? `${currentTheme.primaryColor}15`
                            : '#ecfeff'
                        }}
                      >
                        {currentThemeId !== 'none' ? (
                          <span className="text-lg">{currentTheme.decorativeEmojis[1]}</span>
                        ) : (
                          <ClipboardList className="w-5 h-5 text-cyan-600" strokeWidth={2} />
                        )}
                      </div>
                      <div className="text-left">
                        <div className="flex items-center gap-1 mb-1">
                          <h3 className="text-gray-900 font-semibold text-sm">Protocol</h3>
                          <span 
                            className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase"
                            style={{
                              background: currentThemeId !== 'none'
                                ? `${currentTheme.primaryColor}20`
                                : '#cffafe',
                              color: currentThemeId !== 'none'
                                ? currentTheme.primaryColor
                                : '#0891b2'
                            }}
                          >
                            {currentThemeId !== 'none' ? currentTheme.emoji : 'AI'}
                          </span>
                        </div>
                        <p className="text-gray-500 text-xs">21-day plan</p>
                      </div>
                    </button>
                  )}

                  {/* Custom Habits Button */}
                  <button
                    onClick={() => {
                      if (!userProfile.onboarding_completed) {
                        handleLockedFeatureClick();
                      } else {
                        setShowCustomHabits(true);
                      }
                    }}
                    className="rounded-2xl p-4 flex flex-col justify-between border transition-all active:scale-[0.99]"
                    style={{
                      background: currentThemeId !== 'none'
                        ? `linear-gradient(135deg, white, ${currentTheme.secondaryColor}05)`
                        : 'white',
                      borderColor: currentThemeId !== 'none'
                        ? `${currentTheme.secondaryColor}30`
                        : '#e5e7eb'
                    }}
                  >
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center mb-2"
                      style={{
                        background: currentThemeId !== 'none'
                          ? `${currentTheme.secondaryColor}15`
                          : '#f5f3ff'
                      }}
                    >
                      {currentThemeId !== 'none' ? (
                        <span className="text-lg">{currentTheme.decorativeEmojis[2]}</span>
                      ) : (
                        <Target className="w-5 h-5 text-violet-600" strokeWidth={2} />
                      )}
                    </div>
                    <div className="text-left">
                      <h3 className="text-gray-900 font-semibold text-sm mb-1">Habits</h3>
                      <p className="text-gray-500 text-xs">Track goals</p>
                    </div>
                  </button>
                </div>

                {/* Future Look Button */}
                <div className="w-full">
                  <FutureLookButton
                    protocolGenerated={protocolGenerated || false}
                    onboardingCompleted={userProfile.onboarding_completed || false}
                    onLockedClick={handleLockedFeatureClick}
                  />
                </div>
              </>
            )}

            {/* Nutrition Card with Festival Theme */}
            <div 
              className="rounded-2xl p-5 border transition-all relative overflow-hidden"
              style={{
                background: currentThemeId !== 'none'
                  ? `linear-gradient(135deg, white, ${currentTheme.primaryColor}05)`
                  : 'white',
                borderColor: currentThemeId !== 'none'
                  ? `${currentTheme.primaryColor}20`
                  : '#e5e7eb',
                boxShadow: currentThemeId !== 'none'
                  ? `0 4px 20px ${currentTheme.primaryColor}10`
                  : undefined
              }}
            >
              {/* Decorative corner emoji for festival */}
              {currentThemeId !== 'none' && (
                <div className="absolute top-3 right-3 text-2xl opacity-20">
                  {currentTheme.decorativeEmojis[0]}
                </div>
              )}
              
              <div className="text-center py-6">
                <div className="flex items-baseline justify-center gap-1 mb-2">
                  <span 
                    className="text-5xl font-black"
                    style={{
                      color: currentThemeId !== 'none' && totalCalories >= nutritionGoals.calorie_goal
                        ? currentTheme.primaryColor
                        : '#111827'
                    }}
                  >
                    {totalCalories}
                  </span>
                  <span className="text-2xl text-gray-400 font-medium">/{nutritionGoals.calorie_goal}</span>
                </div>
                <p className="text-sm text-gray-500">
                  {currentThemeId !== 'none' ? `${currentTheme.emoji} calories eaten` : 'calories eaten'}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="flex flex-col items-center">
                  <CircularProgress
                    percentage={(totalProtein / nutritionGoals.protein_goal_g) * 100}
                    size={80}
                    strokeWidth={8}
                    color={currentThemeId !== 'none' ? currentTheme.primaryColor : "#ef4444"}
                  >
                    <span className="text-2xl">🍗</span>
                  </CircularProgress>
                  <div className="mt-2 text-center">
                    <p className="text-xs text-gray-500">Protein</p>
                    <p className="text-sm font-bold text-gray-900">
                      {totalProtein}/{nutritionGoals.protein_goal_g}g
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-center">
                  <CircularProgress
                    percentage={(totalCarbs / nutritionGoals.carbs_goal_g) * 100}
                    size={80}
                    strokeWidth={8}
                    color={currentThemeId !== 'none' ? currentTheme.secondaryColor : "#f97316"}
                  >
                    <span className="text-2xl">🌾</span>
                  </CircularProgress>
                  <div className="mt-2 text-center">
                    <p className="text-xs text-gray-500">Carbs</p>
                    <p className="text-sm font-bold text-gray-900">
                      {totalCarbs}/{nutritionGoals.carbs_goal_g}g
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-center">
                  <CircularProgress
                    percentage={(totalFat / nutritionGoals.fat_goal_g) * 100}
                    size={80}
                    strokeWidth={8}
                    color={currentThemeId !== 'none' ? currentTheme.accentColor : "#3b82f6"}
                  >
                    <span className="text-2xl">🫐</span>
                  </CircularProgress>
                  <div className="mt-2 text-center">
                    <p className="text-xs text-gray-500">Fat</p>
                    <p className="text-sm font-bold text-gray-900">
                      {totalFat}/{nutritionGoals.fat_goal_g}g
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recently Uploaded Foods */}
            <div 
              className="rounded-2xl p-5 border transition-all"
              style={{
                background: currentThemeId !== 'none'
                  ? `linear-gradient(135deg, white, ${currentTheme.primaryColor}03)`
                  : 'white',
                borderColor: currentThemeId !== 'none'
                  ? `${currentTheme.primaryColor}15`
                  : '#e5e7eb'
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-gray-900">
                  {currentThemeId !== 'none' ? `${currentTheme.emoji} Recently uploaded` : 'Recently uploaded'}
                </h2>
                <p className="text-[10px] text-gray-400">Long press to delete</p>
              </div>

              {foodLogs.length === 0 ? (
                <div className="text-center py-8">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3"
                    style={{
                      background: currentThemeId !== 'none'
                        ? `${currentTheme.primaryColor}10`
                        : '#f3f4f6'
                    }}
                  >
                    <span className="text-3xl">
                      {currentThemeId !== 'none' ? currentTheme.decorativeEmojis[3] || '🍽️' : '🍽️'}
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm">No meals logged yet</p>
                  <p className="text-gray-400 text-xs mt-1">Tap the + button to add food</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {foodLogs.map((log) => (
                    <FoodCard
                      key={log.id}
                      food={log}
                      onClick={() => {}}
                      onLongPress={() => handleFoodCardLongPress(log)}
                      onImageClick={() => handleFoodCardClick(log)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Invite Friends Card with Festival Theme */}
            <div 
              className="rounded-2xl p-6 border relative overflow-hidden"
              style={{
                background: currentThemeId !== 'none'
                  ? `linear-gradient(135deg, ${currentTheme.gradientFrom}10, ${currentTheme.gradientTo}10)`
                  : 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                borderColor: currentThemeId !== 'none'
                  ? `${currentTheme.primaryColor}30`
                  : '#a7f3d0'
              }}
            >
              {/* Floating festival emojis */}
              {currentThemeId !== 'none' && (
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  {currentTheme.decorativeEmojis.slice(0, 4).map((emoji, i) => (
                    <motion.span
                      key={i}
                      className="absolute text-2xl opacity-20"
                      style={{
                        left: `${15 + i * 22}%`,
                        top: `${10 + i * 18}%`,
                      }}
                      animate={{
                        y: [0, -8, 0],
                        rotate: [0, 5, -5, 0],
                      }}
                      transition={{
                        duration: 3,
                        delay: i * 0.4,
                        repeat: Infinity,
                      }}
                    >
                      {emoji}
                    </motion.span>
                  ))}
                </div>
              )}
              
              <div className="relative z-10">
                <h3 className="text-sm font-black text-gray-900 uppercase tracking-wide mb-1">
                  {currentThemeId !== 'none' 
                    ? `SHARE THE ${currentTheme.name.toUpperCase()} JOY!`
                    : 'LIVING LONGER IS BORING ALONE.'}
                </h3>
                <p className="text-xs text-gray-600 mb-4">
                  {currentThemeId !== 'none'
                    ? `Invite friends and celebrate ${currentTheme.name} together`
                    : 'Health just feels better with your friends'}
                </p>

                <div className="flex items-center gap-2 mb-4">
                  <div 
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                    style={{
                      background: currentThemeId !== 'none'
                        ? `${currentTheme.primaryColor}20`
                        : '#dcfce7'
                    }}
                  >
                    <Coins 
                      size={14} 
                      strokeWidth={2.5}
                      style={{ color: currentThemeId !== 'none' ? currentTheme.primaryColor : '#16a34a' }}
                    />
                    <span 
                      className="text-xs font-bold"
                      style={{ color: currentThemeId !== 'none' ? currentTheme.primaryColor : '#15803d' }}
                    >
                      {currentThemeId !== 'none' ? `${currentTheme.emoji} EARN 100 POINTS` : 'EARN 50 POINTS'}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (!userProfile.onboarding_completed) {
                        handleLockedFeatureClick();
                      }
                    }}
                    className="flex-1 text-white px-4 py-3 rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
                    style={{
                      background: currentThemeId !== 'none'
                        ? `linear-gradient(135deg, ${currentTheme.gradientFrom}, ${currentTheme.gradientTo})`
                        : '#16a34a'
                    }}
                  >
                    <Share2 size={16} strokeWidth={2.5} />
                    INVITE FRIEND {currentThemeId !== 'none' && currentTheme.emoji}
                  </button>
                  <button
                    onClick={() => {
                      if (!userProfile.onboarding_completed) {
                        handleLockedFeatureClick();
                      }
                    }}
                    className="bg-white hover:bg-gray-50 text-gray-700 px-4 py-3 rounded-xl transition-colors border border-gray-200"
                  >
                    <Share2 size={16} strokeWidth={2.5} />
                  </button>
                </div>
              </div>

              {/* Decorative shape */}
              <div className="absolute -right-2 -bottom-2 w-28 h-28 opacity-20">
                <div className="relative w-full h-full">
                  <div 
                    className="absolute bottom-0 right-0 w-20 h-24 rounded-3xl transform rotate-12"
                    style={{
                      background: currentThemeId !== 'none' ? currentTheme.primaryColor : '#16a34a'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Install App Card */}
            {isInstallable && (
              <div 
                className="rounded-2xl p-5 border"
                style={{
                  background: currentThemeId !== 'none'
                    ? `linear-gradient(135deg, white, ${currentTheme.primaryColor}05)`
                    : 'white',
                  borderColor: currentThemeId !== 'none'
                    ? `${currentTheme.primaryColor}20`
                    : '#e5e7eb'
                }}
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: currentThemeId !== 'none'
                        ? `${currentTheme.primaryColor}15`
                        : '#f3f4f6'
                    }}
                  >
                    {currentThemeId !== 'none' ? (
                      <span className="text-xl">{currentTheme.emoji}</span>
                    ) : (
                      <Download size={22} className="text-gray-700" strokeWidth={2} />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-sm text-gray-900">Get the App</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Install for a better experience</p>
                  </div>
                  <button
                    onClick={handleInstallClick}
                    className="px-5 py-2.5 text-white rounded-xl font-semibold text-sm transition-colors"
                    style={{
                      background: currentThemeId !== 'none'
                        ? `linear-gradient(135deg, ${currentTheme.gradientFrom}, ${currentTheme.gradientTo})`
                        : '#171717'
                    }}
                  >
                    Install
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Floating Action Button with Festival Theme - Disabled for past dates */}
          <button
            onClick={() => {
              if (isSelectedDatePast) {
                return;
              }
              if (!userProfile.onboarding_completed) {
                handleLockedFeatureClick();
              } else {
                setShowAddFood(true);
              }
            }}
            disabled={isSelectedDatePast}
            className={`fixed bottom-24 right-6 w-14 h-14 text-white rounded-full flex items-center justify-center shadow-lg transition-all z-20 ${
              isSelectedDatePast 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:scale-110'
            }`}
            style={{
              background: isSelectedDatePast
                ? '#9ca3af'
                : currentThemeId !== 'none'
                  ? `linear-gradient(135deg, ${currentTheme.gradientFrom}, ${currentTheme.gradientTo})`
                  : '#171717',
              boxShadow: isSelectedDatePast
                ? '0 4px 15px rgba(0,0,0,0.15)'
                : currentThemeId !== 'none'
                  ? `0 8px 25px ${currentTheme.primaryColor}40`
                  : '0 8px 25px rgba(0,0,0,0.3)'
            }}
            title={isSelectedDatePast ? "Can't add food for past dates" : 'Add food'}
          >
            {currentThemeId !== 'none' && !isSelectedDatePast ? (
              <span className="text-xl">{currentTheme.decorativeEmojis[4] || '+'}</span>
            ) : (
              <Plus size={24} strokeWidth={2.5} />
            )}
          </button>

          {showAddFood && (
            <FoodEntrySelection
              onSelectManual={() => {
                setShowAddFood(false);
                setShowManualEntry(true);
              }}
              onSelectAI={() => {
                setShowAddFood(false);
                setShowAIScanner(true);
              }}
              onClose={closeAllModals}
            />
          )}

          {showManualEntry && (
            <ManualFoodEntry
              onSave={handleManualSave}
              onBack={() => {
                setShowManualEntry(false);
                setShowAddFood(true);
              }}
              onClose={closeAllModals}
            />
          )}

          {showAIScanner && (
            <AICameraScanner
              onCapture={handleImageCapture}
              onClose={closeAllModals}
            />
          )}

          {showAIReview && aiResults && (
            <AIResultsReview
              imageData={capturedImage}
              aiResults={aiResults}
              onSave={handleAISave}
              onRetake={handleRetakePhoto}
              onClose={closeAllModals}
              isLoading={isAnalyzing}
            />
          )}

          {isAnalyzing && (
            <AIResultsReview
              imageData={capturedImage}
              aiResults={{ name: '', calories: 0, protein: 0, carbs: 0, fat: 0 }}
              onSave={handleAISave}
              onRetake={handleRetakePhoto}
              onClose={closeAllModals}
              isLoading={true}
            />
          )}

          {/* Bottom Navigation with Festival Theme */}
          <nav 
            className="fixed bottom-0 left-0 right-0 border-t z-30"
            style={{
              background: currentThemeId !== 'none'
                ? `linear-gradient(180deg, ${currentTheme.primaryColor}05, white)`
                : 'white',
              borderColor: currentThemeId !== 'none'
                ? `${currentTheme.primaryColor}15`
                : '#e5e7eb'
            }}
          >
            <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-around">
              <button
                onClick={() => setCurrentPage('home')}
                className="flex flex-col items-center gap-1 min-w-[60px] relative transition-colors"
                style={{ 
                  color: currentPage === 'home' 
                    ? (currentThemeId !== 'none' ? currentTheme.primaryColor : '#06b6d4')
                    : '#9ca3af'
                }}
              >
                {currentPage === 'home' && (
                  <div 
                    className="absolute -top-1 w-12 h-0.5 rounded-full"
                    style={{ 
                      background: currentThemeId !== 'none' ? currentTheme.primaryColor : '#06b6d4'
                    }}
                  />
                )}
                <HomeIcon size={22} strokeWidth={currentPage === 'home' ? 2.5 : 2} />
                <span className="text-[10px] font-bold">Home</span>
              </button>
              <button
                onClick={() => setCurrentPage('progress')}
                className="flex flex-col items-center gap-1 min-w-[60px] relative transition-colors"
                style={{ 
                  color: currentPage === 'progress' 
                    ? (currentThemeId !== 'none' ? currentTheme.primaryColor : '#06b6d4')
                    : '#9ca3af'
                }}
              >
                {currentPage === 'progress' && (
                  <div 
                    className="absolute -top-1 w-12 h-0.5 rounded-full"
                    style={{ 
                      background: currentThemeId !== 'none' ? currentTheme.primaryColor : '#06b6d4'
                    }}
                  />
                )}
                <TrendingUp size={22} strokeWidth={currentPage === 'progress' ? 2.5 : 2} />
                <span className="text-[10px] font-bold">Progress</span>
              </button>
              <button
                onClick={() => setCurrentPage('activity')}
                className="flex flex-col items-center gap-1 min-w-[60px] relative transition-colors"
                style={{ 
                  color: currentPage === 'activity' 
                    ? (currentThemeId !== 'none' ? currentTheme.primaryColor : '#06b6d4')
                    : '#9ca3af'
                }}
              >
                {currentPage === 'activity' && (
                  <div 
                    className="absolute -top-1 w-12 h-0.5 rounded-full"
                    style={{ 
                      background: currentThemeId !== 'none' ? currentTheme.primaryColor : '#06b6d4'
                    }}
                  />
                )}
                <Activity size={22} strokeWidth={currentPage === 'activity' ? 2.5 : 2} />
                <span className="text-[10px] font-bold">Activity</span>
              </button>
              <button
                onClick={() => setShowProfile(true)}
                className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors min-w-[60px]"
              >
                <User size={22} strokeWidth={2} />
                <span className="text-[10px] font-bold">Profile</span>
              </button>
            </div>
          </nav>

          {showCompleteProfileModal && (
            <CompleteProfileModal
              onComplete={handleStartOnboarding}
              onClose={() => setShowCompleteProfileModal(false)}
            />
          )}

          {showGenerateProtocol && (
            <GenerateProtocolModal
              onComplete={handleProtocolGenerated}
              onClose={() => setShowGenerateProtocol(false)}
              onUpgrade={() => {
                setShowGenerateProtocol(false);
                setShowSubscription(true);
              }}
            />
          )}

          {selectedFood && (
            <FoodDetailModal
              food={selectedFood}
              onClose={() => setSelectedFood(null)}
            />
          )}

          {foodToDelete && (
            <DeleteFoodModal
              food={foodToDelete}
              onClose={() => setFoodToDelete(null)}
              onConfirm={handleDeleteFood}
              isDeleting={isDeleting}
            />
          )}
        </div>
      </div>
    </>
  );
}